import React, { useEffect, useState } from "react";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import PERMISSIONS, { apiFetch, canAccessFolder, validateFolderAccess } from "../utils/wkCommon";
import { getUser, isLogged } from "../utils/auth";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import { isZh } from "../utils/language";
import { R2_PUBLIC_BASE_URL } from "../config/storage";
import { useLocation } from "react-router-dom";

import FolderTree from "../docs/FolderTree";
import DocumentList from "../docs/DocumentList";
import DocumentPreview from "../docs/DocumentPreview";
import UploadDialog from "../docs/UploadDialog";
import ConfirmDangerDialog from "../docs/ConfirmDangerDialog";
import FileFormDialog from "../docs/FileFormDialog";
import FileInfoDialog from "../docs/FileInfoDialog";
import FolderInfoDialog from "../docs/FolderInfoDialog";

export default function DocumentsPage() {
  const user = getUser();
  const isPublic = !isLogged();  
  const layoutVariant = isPublic ? "oneColumn" : "right60";

  const [canEdit, setCanEdit] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [folders, setFolders] = useState([]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [showProperties, setShowProperties] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [allFolders, setAllFolders] = useState([]);

  const [loading, setLoading] = useState(false);

  const location = useLocation();

  useEffect(() => {
    async function loadAllFolders() {
      const res = await apiFetch(
        "/api/document-folders?populate[0]=document_folder&populate[1]=allowed_roles"
      );
      const filtered = (res?.data || []).filter(f =>
        canAccessFolder(f, user)
      );

      setAllFolders(filtered);
    }

    loadAllFolders();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const folderId = params.get("folder");

    if (!folderId || !allFolders.length) return;

    const found = allFolders.find(
      f => f.documentId === folderId
    );

    if (found) {
      setActiveFolder(found);
    }
  }, [location.search, allFolders]);

  //auto-clear selected saat pindah folder
  useEffect(() => {
    setSelectedDoc(null);
  }, [activeFolder]);

  useEffect(() => {
    if (searchTerm.trim()) {
      setSelectedDoc(null);
    }
  }, [searchTerm]);

  useEffect(() => {
    let mounted = true;
    async function checkPerm() {
      if (isPublic) {
        if (mounted) setCanEdit(false);
        return;
      }

      const ok = await hasUmatPermissionAsync(
        user?.email,
        PERMISSIONS.MEDIAFILE_EDITOR
      );
      if (mounted) setCanEdit(Boolean(ok));
    }
    checkPerm();
    return () => (mounted = false);
  }, [user, isPublic]);

  async function loadData() {
    setLoading(true);

    // ❗ reset data lama supaya tidak flicker
    setDocuments([]);
    setFolders([]);

    // ======================
    // LOAD DOCUMENTS
    // ======================

    let docUrl =
      "/api/document-files?populate=document_folder&filters[access_level][$eq]=public&sort[0]=title_id:asc";

    if (searchTerm.trim()) {
      const q = encodeURIComponent(searchTerm.trim());

      docUrl +=
        `&filters[$or][0][title_id][$containsi]=${q}` +
        `&filters[$or][1][title_zh][$containsi]=${q}`;
    } else {
      if (activeFolder) {
        docUrl += `&filters[document_folder][documentId][$eq]=${encodeURIComponent(activeFolder.documentId)}`;
      } else {
        docUrl += `&filters[document_folder][$null]=true`;
      }
    }

    // 🔎 SEARCH FILTER
    if (searchTerm) {
      const q = encodeURIComponent(searchTerm);

      docUrl +=
        `&filters[$or][0][title_id][$containsi]=${q}` +
        `&filters[$or][1][title_zh][$containsi]=${q}`;
    }

    //console.log("DOC URL:", docUrl);
    const docRes = await apiFetch(docUrl);
    setDocuments(docRes?.data || []);

    // ======================
    // LOAD SUBFOLDERS
    // ======================

    if (!searchTerm.trim()) {
          let folderUrl =
            "/api/document-folders?populate[0]=document_folder&populate[1]=allowed_roles&sort=name:asc";
            
          if (activeFolder) {
            folderUrl += `&filters[document_folder][documentId][$eq]=${encodeURIComponent(activeFolder.documentId)}`;
          } else {
            folderUrl += `&filters[document_folder][$null]=true`;
          }

          const folderRes = await apiFetch(folderUrl);

          const filtered = (folderRes?.data || []).filter(f =>
            canAccessFolder(f, user)
          );

          setFolders(filtered);
      } else {
      setFolders([]); // kosongkan saat search
    }

    setLoading(false);
  }
    
  useEffect(() => {
    loadData();
  }, [activeFolder, searchTerm]);

  function goUp() {
    if (!activeFolder) return;
    setActiveFolder(activeFolder.document_folder || null);
  }

  const [showFolderInfo, setShowFolderInfo] = useState(false);
  function buildBreadcrumb(folder) {
    if (!folder) return [];

    const trail = [];
    let current = folder;

    while (current) {
      trail.unshift(current);
      current = current.document_folder || null;
    }

    return trail;
  }
  function getCollapsedBreadcrumb(trail) {
    if (!trail || trail.length === 0) return [];

    if (trail.length <= 3) return trail;

    return [
      trail[0],
      { isEllipsis: true },
      trail[trail.length - 1],
    ];
  }

  async function handleDelete(doc) {
    setLoading(true);

    await apiFetch("/api/r2-delete", {
      method: "POST",
      body: { objectKey: doc.object_key },
    });

    await apiFetch(`/api/document-files/${doc.documentId}`, {
      method: "DELETE"
    });

    setSelectedDoc(null);

    await loadData(); // 🔥 reload beneran

    setLoading(false);
  }

  const breadcrumbTrail = buildBreadcrumb(activeFolder);  
  const collapsedTrail = getCollapsedBreadcrumb(breadcrumbTrail);

  const pageTitle = activeFolder
    ? (isZh() ? activeFolder.name_zh : activeFolder.name)?.toUpperCase()
    :  (isZh() ? "禮恩檔案" : "Li En Fo Yuan: Files");

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <TwoColumnLayout
      variant={layoutVariant}
      title={pageTitle}
      showBack={!isPublic}
      headerActions={null} // hide default + / ⋯
      layoutClass="files-layout"
      leftNode={
        canEdit && (
          <div className="doc-left-panel">
            <div className="doc-section-title">Folders</div>
            <FolderTree
              user={user}
              activeFolder={activeFolder}
              onSelect={(folder) => {
                setSearchTerm("");     // reset search
                setActiveFolder(folder);
              }}
            />
          </div>
        )
      }
      rightNode={
        <div className="doc-main-panel">
          <div className="doc-header-row">
            <div className="doc-title">
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  className="doc-search"
                  placeholder={isZh() ? "搜尋檔案..." : "Search documents..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    style={{
                      position: "absolute",
                      right: "0.6rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "1rem",
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {canEdit && activeFolder && (
              <div className="doc-actions">
                <button
                  className="icon-btn primary"
                  onClick={() => setShowUpload(true)}
                  style={{ width: "8rem", fontSize: "1rem", gap: "0.5rem" }}
                >
                  <img src="/icons/file-add.png" alt="Upload File" /> Upload
                </button>
              </div>
            )}
          </div>

          {activeFolder && (
            <div className="doc-toolbar">
              <div className="doc-toolbar-left">
                <button
                  className="icon-btn"
                  onClick={() => setShowFolderInfo(true)}
                >
                  ⓘ
                </button>

                {searchTerm ? (
                  <div className="search-indicator">
                    {isZh()
                      ? `搜尋結果：${searchTerm}`
                      : `Search results for: "${searchTerm}"`}
                  </div>
                ) : (
                  <div className="breadcrumb">
                    {collapsedTrail.map((b, idx) => {
                      if (b.isEllipsis) {
                        return <span key="ellipsis"> ... </span>;
                      }

                      return (
                        <span key={b.documentId}
                          className="breadcrumb-item"
                          onClick={() => {
                            setSearchTerm("");
                            setActiveFolder(b);
                          }}
                        >
                          {isZh() ? b.name_zh : b.name}
                          {idx < collapsedTrail.length - 1 && " / "}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedDoc && (
                <div className="doc-toolbar-right">
                  {/* Copy Link */}
                  <button
                    className="icon-btn"
                    title="Copy Link"
                    onClick={() => {
                      const link = `${window.location.origin}/filesdirect?document=${selectedDoc.documentId}`;
                      navigator.clipboard.writeText(link);
                    }}
                  >
                    <img src="/icons/link-copy.png" alt="Copy Link" />
                  </button>

                  {/* Share */}
                  <button
                    className="icon-btn"
                    title="Share"
                    onClick={() => {
                      const link = `${window.location.origin}/filesdirect?document=${selectedDoc.documentId}`;

                      if (navigator.share) {
                        navigator.share({
                          title: selectedDoc.title_id,
                          url: link,
                        });
                      } else {
                        navigator.clipboard.writeText(link);
                      }
                    }}
                  >
                    <img src="/icons/share.png" alt="Share" />
                  </button>

                  <button
                    className="icon-btn"
                    title="Download"
                    onClick={async () => {
                      const ext = selectedDoc.object_key?.split(".").pop();
                      const title = isZh()
                        ? selectedDoc.title_zh
                        : selectedDoc.title_id;

                      const filename = ext ? `${title}.${ext}` : title;
                      const url = `${R2_PUBLIC_BASE_URL}/${selectedDoc.object_key}`;

                      try {
                        const response = await fetch(url);
                        const blob = await response.blob();

                        const objectUrl = URL.createObjectURL(blob);

                        const link = window.document.createElement("a");
                        link.href = objectUrl;
                        link.download = filename;

                        window.document.body.appendChild(link);
                        link.click();
                        window.document.body.removeChild(link);

                        URL.revokeObjectURL(objectUrl);
                      } catch (err) {
                        console.error("Download failed:", err);
                      }
                    }}
                  >
                    <img src="/icons/file-download.png" alt="Download" />
                  </button>

                  <button
                    className="icon-btn"
                    title="Preview"
                    onClick={() => setPreviewDoc(selectedDoc)}
                  >
                    <img src="/icons/link-share.png" alt="Open/preview" />
                  </button>

                  {canEdit && (
                    <div className="menu-wrapper">
                      <button className="icon-btn" onClick={() => setMenuOpen(prev => !prev)}> ⋮ </button>

                      {menuOpen && (
                        <div className="action-menu">

                          <div className="menu-item" onClick={() => { setEditingFile(selectedDoc); setMenuOpen(false); }}>
                            <span className="menu-icon">
                              <img src="/icons/file-rename.png" alt="Rename" />
                            </span>
                            <span className="menu-label">Rename</span>
                          </div>

                          <div
                            className="menu-item"
                            onClick={() => {
                              setShowProperties(selectedDoc);
                              setMenuOpen(false);
                            }}
                          >
                            <span className="menu-icon">
                              <img src="/icons/file-info.png" alt="File Properties" />
                            </span>
                            <span className="menu-label">File Properties</span>
                          </div>

                          <div
                            className="menu-item danger"
                            onClick={() => {
                              setToDelete(selectedDoc);
                              setMenuOpen(false);
                            }}
                          >
                            <span className="menu-icon">
                              <img src="/icons/file-delete.png" alt="Delete" />
                            </span>
                            <span className="menu-label">Delete</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          <DocumentList
            user={user}
            documents={documents}
            folders={folders}
            canEdit={canEdit}
            activeFolder={activeFolder}
            selectedDoc={selectedDoc}
            onSelectDoc={setSelectedDoc}
            onPreview={setPreviewDoc}
            onOpenFolder={setActiveFolder}
            onGoUp={goUp}
            loading={loading}
            onDelete={handleDelete}
          />

          {previewDoc && (
            <DocumentPreview
              document={previewDoc}
              onClose={() => setPreviewDoc(null)}
            />
          )}

          {showUpload && (
            <UploadDialog
              user={user}
              currentFolder={activeFolder}
              onClose={() => setShowUpload(false)}
              onUploaded={async () => {
                setShowUpload(false);
                await loadData();
              }}
            />
          )}

          {toDelete && (
            <ConfirmDangerDialog
              title="Delete File"
              message={`Delete "${toDelete.title_id}"?`}
              onConfirm={async () => {
                await handleDelete(toDelete);
                setToDelete(null);
              }}
              onClose={() => setToDelete(null)}
            />
          )}

          {editingFile && (
            <FileFormDialog
              user={user}
              file={editingFile}
              onClose={() => setEditingFile(null)}
              onSaved={() => {
                setEditingFile(null);
                setActiveFolder(activeFolder);
              }}
            />
          )}

          {showFolderInfo && activeFolder && (
            <FolderInfoDialog
              folder={activeFolder}
              onClose={() => setShowFolderInfo(false)}
            />
          )}

          {showProperties && (
            <FileInfoDialog
              file={showProperties}
              onClose={() => setShowProperties(null)}
              showShareActions={true}
            />
          )}
        </div>
      }
    />
  );

}
